import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import type { RootState } from '~/store';
import { Idea, IdeaEvents } from '../types';

const ideasAdapter = createEntityAdapter<Idea>({
  sortComparer: (a: Idea, b: Idea) => (b.createdAt || 0) - (a.createdAt || 0),
});

export const ideasSlice = createSlice({
  name: 'ideas',
  initialState: ideasAdapter.getInitialState<{ fromExtension: boolean }>({
    fromExtension: false,
  }),
  reducers: {
    ideaAdded: ideasAdapter.addOne,
    ideaUpdated: ideasAdapter.setOne,
    ideasReceived: ideasAdapter.setMany,
    ideaDeleted: ideasAdapter.removeOne,
    ideasDeleted: ideasAdapter.removeMany,
    markExtension: (
      state,
      action: PayloadAction<{ fromExtension: boolean }>
    ) => {
      state.fromExtension = action.payload.fromExtension;
    },
  },
  extraReducers: {
    [`PUSHER_EVENT/${IdeaEvents.IDEAS_DELETED}`]: (state, action) => {
      ideasAdapter.removeMany(
        state,
        action.data?.ideas?.map((i) => i.id)
      );
    },
    [`PUSHER_EVENT/${IdeaEvents.IDEA_CREATED}`]: (state, action) => {
      ideasAdapter.addOne(state, action.data.idea);
    },
    [`PUSHER_EVENT/${IdeaEvents.IDEA_UPDATED}`]: (state, action) => {
      ideasAdapter.setOne(state, action.data.idea);
    },
  },
});

const { ideasReceived, ideasDeleted, markExtension } = ideasSlice.actions;

// globalized selectors
const { selectById: selectIdeaById, selectAll: selectAllIdeas } =
  ideasAdapter.getSelectors((state: RootState) => state[ideasSlice.name]);

// custom selectors
const selectIsIdeaFromExtension = (state: RootState) =>
  state[ideasSlice.name].fromExtension;

export {
  ideasReceived,
  selectIdeaById,
  selectAllIdeas,
  ideasDeleted,
  markExtension,
  selectIsIdeaFromExtension,
};
